import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  Modal,
  Row,
  Col,
  Button,
  Badge,
  Card,
  Container,
} from "react-bootstrap";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { ThreeDots, RotatingLines } from "react-loader-spinner";
import {
  Package2,
  MapPin,
  ArrowLeft,
  Grid3x3,
  PackageCheck,
  CheckCircle,
  XCircle,
  AlertCircle,
} from "lucide-react";
import AlertNotificationSimple from "../alert/alert-notification-simple";
import { roleTypes } from "../../data/roleType";
import url from "../../configs/config";
import { useSelector } from "react-redux";
import * as SettingSelector from "../../store/setting/selectors";

const RackBinModal = ({
  show,
  onHide,

  selectedRackProduct,
  warehouseProducts,
  handleBinAssignToWarehouseProduct,
  setSelectedRackProduct,

  selectedRackOrderItem, 
  handleBinAssign,      
  warehouseId: warehouseIdProp,
  pendingBinIds = {},

  token,
  decodedToken,
  alerts: alertResponse,
  setAlertResponse,
}) => {
  const params = useParams();
  const urlId = params?.id;
  const warehouseId = warehouseIdProp ?? urlId;

  const navigate = useNavigate();
  const alertRef = useRef(null);
  const currentLocationRef = useRef(null);

  const isOrderMode = !!selectedRackOrderItem;

  const activeProduct = isOrderMode
    ? {
        id: selectedRackOrderItem?.product_id ?? selectedRackOrderItem?.id,
        name: selectedRackOrderItem?.name ?? selectedRackOrderItem?.productName,
        product_quantity: 0,
        default_bin_id: selectedRackOrderItem?.default_bin_id ?? selectedRackOrderItem?.rack_id ?? null,
        rack_id: selectedRackOrderItem?.rack_id_parent ?? null,
        warehouse_product_id: selectedRackOrderItem?.order_item_id ?? null,
      }
    : selectedRackProduct;

  const [currentView, setCurrentView] = useState("racks");
  const [loader, setLoader] = useState(true);
  const [selectedRack, setSelectedRack] = useState(null);
  const [selectedBins, setSelectedBins] = useState(null);
  const [allRacks, setAllRacks] = useState([]);
  const [filteredRacks, setFilteredRacks] = useState([]);
  const [bins, setBins] = useState([]);
  const [hoveredCard, setHoveredCard] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [entriesPerPage] = useState(12);
  const [searchTerm, setSearchTerm] = useState("");
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  const [animatingId, setAnimatingId] = useState(null);
  const [rackBinLoader, setRackBinLoader] = useState(true);
  const [overrideBinId, setOverrideBinId] = useState(null);
  const [overrideRackId, setOverrideRackId] = useState(null);
  
  useEffect(() => {
    if (show) {
      setOverrideBinId(null);
      setOverrideRackId(null);
      setCurrentView("racks");
      setSelectedRack(null);
      setBins([]);
      setSelectedBins(null);
      setSearchTerm("");
      setCurrentPage(1);
      setIsInitialLoad(true);
    }
  }, [show]);

  const [alerts, setAlerts] = useState({
    message: "",
    type: "",
    show: false,
    alertFor: "",
  });

  const permissions = useSelector(SettingSelector.set_permissions);
  const rackManagementHasCreatePermission =
    permissions?.rack_management?.create === true;

  const totalPages = Math.ceil(filteredRacks.length / entriesPerPage);
  const startIndex = (currentPage - 1) * entriesPerPage;
  const paginatedRacks = filteredRacks.slice(
    startIndex,
    startIndex + entriesPerPage,
  );

  const filterRacks = useCallback((racks, search) => {
    if (!search || search.trim().length < 2) return racks;
    const s = search.toLowerCase().trim();
    return racks.filter(
      (r) =>
        r.name?.toLowerCase().includes(s) ||
        r.rack_number?.toLowerCase().includes(s) ||
        r.description?.toLowerCase().includes(s),
    );
  }, []);

  useEffect(() => {
    setFilteredRacks(filterRacks(allRacks, searchTerm));
    setCurrentPage(1);
  }, [searchTerm, allRacks, filterRacks]);

  const redirectToLogin = () => {
    localStorage.removeItem("coyote-token");
    navigate(
      `/${
        decodedToken?.roles?.some(
          (r) => r.role_type === roleTypes.SUPER_ADMIN,
        )
          ? "admin"
          : "user"
      }/auth/sign-in`,
      { state: { status: "invalid-token" } },
    );
  };

  const handleError = (err, alertFor) => {
    const { response } = err;
    if (
      response?.data?.error === "jwt expired" ||
      response?.data?.error === "Unauthorized. User not found"
    ) {
      localStorage.removeItem("coyote-token");
      navigate(
        `/${
          decodedToken?.roles?.some(
            (r) => r.role_type === roleTypes.SUPER_ADMIN,
          )
            ? "admin"
            : "user"
        }/auth/sign-in`,
        { state: { status: "expired" } },
      );
    } else {
      setAlerts({
        message: response ? response.data.message : "An error occurred",
        type: "danger",
        show: true,
        alertFor,
      });
      setTimeout(() => {
        setAlerts({ show: false, alertFor: "", message: "", type: "" });
      }, 5000);
    }
  };

  const getWarehouseRacks = async () => {
    if (!token) { redirectToLogin(); setLoader(false); return; }
    if (!warehouseId) { setLoader(false); return; }

    try {
      const response = await axios.post(
        `${url.backendURL}warehouse/${warehouseId}/racks/active`,
        { productId: activeProduct?.id },
        { headers: { Authorization: token } },
      );
      const data = response?.data?.response?.data || [];
      setAllRacks(data);
      setFilteredRacks(data);
    } catch (err) {
      handleError(err, "warehouses-racks");
    } finally {
      setLoader(false);
    }
  };

  const getWarehouseRackBins = async (rackId) => {
    if (!token) { redirectToLogin(); setRackBinLoader(false); return; }

    const requestBody = { productId: activeProduct?.id };

    try {
      const response = await axios.post(
        `${url.backendURL}warehouse/${warehouseId}/racks/${rackId}/active`,
        requestBody,
        { headers: { Authorization: token } },
      );
      const data = response?.data?.response?.data;
      setBins(data?.[0]?.bins || []);
    } catch (err) {
      handleError(err, "warehouses-rack-bin-edit");
    } finally {
      setRackBinLoader(false);
    }
  };

  useEffect(() => {
    if (show && currentView === "racks" && isInitialLoad) {
      setLoader(true);
      getWarehouseRacks();
      setIsInitialLoad(false);
    }
  }, [show, currentView, isInitialLoad]);

  useEffect(() => {
    if (show && currentLocationRef.current && paginatedRacks.length > 0) {
      setTimeout(() => {
        currentLocationRef.current?.scrollIntoView({
          behavior: "smooth",
          block: "center",
        });
      }, 300);
    }
  }, [show, paginatedRacks]);

  useEffect(() => {
    if (!show) {
      setCurrentView("racks");
      setSelectedRack(null);
      setSelectedBins(null);
      setSearchTerm("");
      setCurrentPage(1);
      setIsInitialLoad(true);
      setAllRacks([]);
      setFilteredRacks([]);
    }
  }, [show]);

  const handleRackClick = async (rack) => {
    setSelectedRack(rack);
    setCurrentView("bins");
    setRackBinLoader(true);
    setSelectedBins(null);
    await getWarehouseRackBins(rack.rack_id);
  };

  const handleBackToRacks = () => {
    setSelectedRack(null);
    setSelectedBins(null);
    setCurrentView("racks");
  };

  const handleModalClose = () => {
    setCurrentView("racks");
    setSelectedRack(null);
    setSelectedBins(null);
    setSearchTerm("");
    setCurrentPage(1);
    onHide();
  };

  const handleBinClick = async (binId, containsSelectedProduct) => {
    if (!activeProduct?.id) return;
    const bin = bins.find((b) => b.id === binId);
    if (!bin) { setSelectedBins(null); return; }

    setAnimatingId(binId);

    if (isOrderMode) {
      const result = await handleBinAssign(selectedRack, bin, containsSelectedProduct);
      if (result?.message) {
        setAlerts({
          message: result.message,
          type: result.success ? "success" : "danger",
          show: true,
          alertFor: "bin-list",
        });
        setTimeout(() => {
          setAlerts({ show: false, alertFor: "", message: "", type: "" });
        }, 5000);
      }
      if (result?.success) {
        setOverrideBinId(binId);
        setOverrideRackId(selectedRack.rack_id);
        if (selectedRack?.rack_id) {
          await getWarehouseRackBins(selectedRack.rack_id);
        }
      }
    } else {
      await handleBinAssignToWarehouseProduct(
        selectedRack,
        bin,
        containsSelectedProduct,
      );
    }

    setAnimatingId(null);
    setSelectedBins(binId);
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case "active":    return <CheckCircle size={16} className="text-success" />;
      case "in-active": return <XCircle size={16} className="text-danger" />;
      case "damaged":   return <AlertCircle size={16} className="text-danger" />;
      case "filled":    return <PackageCheck size={16} className="text-primary" />;
      default:          return <CheckCircle size={16} className="text-secondary" />;
    }
  };

  const binContainsActiveProduct = (binId, bin) => {
    const effectiveBinId = overrideBinId || activeProduct?.default_bin_id;
    return effectiveBinId
      ? Number(effectiveBinId) === Number(binId)
      : !!bin?.is_current_product_default;
  };

  const getBinProduct = (binId, bin) => {
    if (binContainsActiveProduct(binId, bin)) {
      return {
        id: activeProduct.id,
        product_name: activeProduct.name || "Selected Product",
        quantity: activeProduct.product_quantity || 0,
      };
    }
    
    if (bin?.product_id) {
      return {
        id: bin.product_id,
        product_name: bin.product_name || "Unknown Product",
        quantity: 0,
      };
    }

    if (!isOrderMode && warehouseProducts) {
      const match = warehouseProducts.find(
        (p) =>
          p.default_bin_id === binId &&
          p.id !== activeProduct?.id,
      );
      if (match) {
        return {
          id: match.id,
          product_name: match.name,
          quantity: match.product_quantity || 0,
        };
      }
    }
    return null;
  };

  const rackContainsActiveProduct = (rackId, rack) => {
    const effectiveRackId = overrideRackId || activeProduct?.rack_id;
    if (effectiveRackId && rackId) {
      return Number(effectiveRackId) === Number(rackId);
    }
    return !!rack?.has_current_product;
  };

  const generateBinGrid = (rack) => {
    const grid = [];
    for (let row = rack.total_rows; row >= 1; row--) {
      for (let col = 1; col <= rack.total_columns; col++) {
        const bin = bins.find(
          (b) => b.row_number === row && b.column_number === col,
        );
        if (bin) {
          const product = getBinProduct(bin.id, bin);
          grid.push({
            row,
            col,
            id: bin.id,
            bin,
            product,
            hasProduct: !!product,
            status: bin.status,
            key: `${row}-${col}`,
          });
        }
      }
    }
    return grid;
  };

  const handleAddNewRack = () => {
    setCurrentView(null);
    if (setSelectedRackProduct) setSelectedRackProduct(null);
    onHide(false);
    window.open(`/dashboard/app/rack-add?id=${warehouseId}`, "_blank");
  };

  const LoadingSpinner = () => (
    <div
      className="d-flex flex-column align-items-center justify-content-center"
      style={{ minHeight: "400px", padding: "2rem" }}
    >
      <ThreeDots
        height="60" width="60" radius="9" color="#3a57e8"
        ariaLabel="three-dots-loading" visible
      />
      <p className="text-muted mt-3 mb-0">Loading...</p>
    </div>
  );

  const NoRacksFound = () => (
    <div
      className="d-flex flex-column align-items-center justify-content-center text-center"
      style={{ minHeight: "400px", padding: "2rem" }}
    >
      <Package2 className="text-muted mb-3" size={64} />
      <h4 className="text-dark mb-2">No Racks Found</h4>
      <p className="text-muted mb-3">
        {searchTerm ? (
          <>
            No racks match your search.
            {(rackManagementHasCreatePermission ||
              decodedToken?.roles?.some(
                (r) => r.role_type === roleTypes.SUPER_ADMIN,
              )) && (
              <>
                {" or "}
                <a
                  href="#"
                  onClick={(e) => { e.preventDefault(); handleAddNewRack(); }}
                  className="text-primary text-decoration-none"
                  style={{ fontWeight: "500", cursor: "pointer" }}
                >
                  Install Racks
                </a>
              </>
            )}
          </>
        ) : (
          <>
            There are no racks available in this warehouse
            {(rackManagementHasCreatePermission ||
              decodedToken?.roles?.some(
                (r) => r.role_type === roleTypes.SUPER_ADMIN,
              )) && (
              <>
                ,{" "}
                <a
                  href="#"
                  onClick={(e) => { e.preventDefault(); handleAddNewRack(); }}
                  className="text-decoration-none"
                  style={{ fontWeight: "500", cursor: "pointer" }}
                >
                  click here
                </a>{" "}
                to install some racks
              </>
            )}
          </>
        )}
      </p>
    </div>
  );

  const NoBinsFound = () => (
    <div
      className="d-flex flex-column align-items-center justify-content-center text-center"
      style={{ minHeight: "400px", padding: "2rem" }}
    >
      <Package2 className="text-muted mb-3" size={64} />
      <h4 className="text-dark mb-2">No Bins Found</h4>
      <p className="text-muted mb-0">This rack doesn&apos;t have any bins.</p>
    </div>
  );

  const RacksView = () => (
    <div style={{ minHeight: "400px" }}>
      {loader ? (
        <LoadingSpinner />
      ) : paginatedRacks?.length > 0 ? (
        <Row className="g-3 mt-2">
          {paginatedRacks.map((rack) => {
            const hasSelectedProduct = rackContainsActiveProduct(rack.rack_id, rack);
            const isCurrentlySelected = selectedRack?.rack_id === rack.rack_id;
            const isActive = hasSelectedProduct || isCurrentlySelected;
            const [showTooltip, setShowTooltip] = useState(null);

            const tooltipStyle = (rackId) => ({
              position: "absolute",
              bottom: "calc(100% + 10px)",
              left: "20%",
              transform: "translateX(-50%)",
              backgroundColor: "#1f2937",
              color: "white",
              padding: "0.5rem 0.75rem",
              borderRadius: "6px",
              fontSize: "0.8rem",
              fontWeight: "500",
              whiteSpace: "normal",
              zIndex: 1000,
              opacity: showTooltip === rackId ? 1 : 0,
              visibility: showTooltip === rackId ? "visible" : "hidden",
              transition: "all 0.3s ease",
              boxShadow: "0 4px 12px rgba(0,0,0,0.2)",
            });

            const tooltipArrowStyle = {
              position: "absolute",
              top: "100%",
              left: "50%",
              transform: "translateX(-50%)",
              width: 0,
              height: 0,
              borderLeft: "6px solid transparent",
              borderRight: "6px solid transparent",
              borderTop: "6px solid #1f2937",
            };

            return (
              <Col
                xs={12} sm={6} md={6} xl={4}
                key={rack.id}
                onClick={() => rack.status === "active" && handleRackClick(rack)}
                onMouseEnter={() => {
                  setHoveredCard(rack.id);
                }}
                onMouseLeave={() => setHoveredCard(null)}
                ref={hasSelectedProduct ? currentLocationRef : null}
              >
                <Card
                  style={{
                    transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                    borderRadius: "16px",
                    height: "200px",
                    transform: isActive
                      ? "translateY(-8px) scale(1.05)"
                      : "translateY(0) scale(1)",
                    cursor: rack.status !== "active" ? "not-allowed" : "pointer",
                    border: "2px solid",
                    opacity: rack.status !== "active" ? 0.7 : 1,
                    pointerEvents: rack.status !== "active" ? "none" : "auto",
                    borderColor: isActive ? "#3a57e8" : "transparent",
                    position: "relative",
                  }}
                  className="h-100 shadow-sm"
                >
                  {hasSelectedProduct && (
                    <div
                      style={{
                        position: "absolute",
                        top: "-1rem",
                        left: "50%",
                        transform: "translateX(-50%)",
                        zIndex: 10,
                      }}
                    >
                      <Badge
                        bg="primary"
                        style={{
                          fontSize: "0.7rem",
                          padding: "0.4rem 0.8rem",
                          borderRadius: "12px",
                          fontWeight: "600",
                          boxShadow: "0 2px 8px rgba(0,0,0,0.15)",
                        }}
                      >
                        Current Location
                      </Badge>
                    </div>
                  )}

                  {rack.status !== "active" && (
                    <>
                      <div
                        style={{
                          position: "absolute",
                          top: 0, left: 0, right: 0,
                          height: "50%",
                          backgroundColor: "#ffffff66",
                          borderRadius: "16px 16px 0 0",
                          zIndex: 20,
                        }}
                      />
                      <div
                        style={{
                          position: "absolute",
                          bottom: 0, left: 0, right: 0,
                          height: "50%",
                          backgroundColor: "#ffffff66",
                          borderRadius: "0 0 16px 16px",
                          zIndex: 20,
                        }}
                      />
                      <div
                        style={{
                          position: "absolute",
                          top: "50%", left: "50%",
                          transform: "translate(-50%, -50%)",
                          zIndex: 21,
                        }}
                      >
                        <div
                          style={{
                            backgroundColor: "#dc3545",
                            padding: "0.5rem 0.8rem",
                            borderRadius: "8px",
                            textAlign: "center",
                          }}
                        >
                          <div
                            style={{
                              color: "white",
                              fontSize: "0.60rem",
                              textTransform: "uppercase",
                              letterSpacing: "0.4px",
                              fontWeight: "600",
                              whiteSpace: "nowrap",
                            }}
                          >
                            {rack.status.replace("-", " ")}
                          </div>
                        </div>
                      </div>
                    </>
                  )}

                  <Card.Body
                    className="p-3 d-flex flex-column position-relative"
                    style={{ minHeight: "176px" }}
                  >
                    <div
                      className="d-flex justify-content-between align-items-center mb-4"
                      style={{ height: "64px" }}
                    >
                      <div
                        className="d-flex align-items-start gap-2 flex-grow-1"
                        style={{ minWidth: 0 }}
                      >
                        <div
                          className="mt-auto p-2 rounded-3 d-flex align-items-center justify-content-center flex-shrink-0"
                          style={{
                            backgroundColor:
                              hasSelectedProduct || isCurrentlySelected
                                ? "#3a57e8"
                                : "#198754",
                            width: "36px",
                            height: "36px",
                          }}
                        >
                          <Grid3x3 size={18} className="text-white" />
                        </div>
                        <div style={{ minWidth: 0, flex: 1 }}>
                          <div
                            style={{ position: "relative" }}
                            onMouseEnter={() => setShowTooltip(rack.id)}
                            onMouseLeave={() => setShowTooltip(null)}
                          >
                            <h6
                              className="mb-1 fw-bold text-dark"
                              style={{
                                fontSize: "0.95rem",
                                whiteSpace: "nowrap",
                                overflowX: "auto",
                                maxWidth: "90%",
                                cursor: "help",
                                lineHeight: "1.2",
                                scrollbarWidth: "none",
                                msOverflowStyle: "none",
                                marginTop: "8px",
                              }}
                            >
                              {rack.name}
                            </h6>
                            <div style={tooltipStyle(rack.id)}>
                              {rack.name}
                              <div style={tooltipArrowStyle}></div>
                            </div>
                          </div>
                          <small
                            className="text-muted fw-medium d-block"
                            style={{
                              whiteSpace: "nowrap",
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              lineHeight: "1.2",
                            }}
                          >
                            {rack.rack_number}
                          </small>
                        </div>
                      </div>
                      <div className="d-flex align-items-center justify-content-center flex-shrink-0">
                        {getStatusIcon(rack.status)}
                      </div>
                    </div>

                    <div className="row g-2 mb-3" style={{ height: "60px" }}>
                      <div className="col-6">
                        <div className="text-center p-2 bg-light rounded-3 h-100 d-flex flex-column justify-content-center">
                          <div className="fw-bold text-primary h6 mb-0" style={{ fontSize: "1rem" }}>
                            {rack.total_rows}
                          </div>
                          <small className="text-muted fw-medium" style={{ fontSize: "0.75rem" }}>Rows</small>
                        </div>
                      </div>
                      <div className="col-6">
                        <div className="text-center p-2 bg-light rounded-3 h-100 d-flex flex-column justify-content-center">
                          <div className="fw-bold text-primary h6 mb-0" style={{ fontSize: "1rem" }}>
                            {rack.total_columns}
                          </div>
                          <small className="text-muted fw-medium" style={{ fontSize: "0.75rem" }}>Columns</small>
                        </div>
                      </div>
                    </div>

                    <div className="mt-auto" style={{ height: "40px" }}>
                      <Button
                        variant="outline-primary"
                        size="sm"
                        className="w-100 h-100 d-flex align-items-center justify-content-center gap-2"
                        disabled={rack.status !== "active"}
                        style={{
                          borderRadius: "10px",
                          fontWeight: "500",
                          fontSize: "0.85rem",
                        }}
                        onClick={() => rack.status === "active" && handleRackClick(rack)}
                      >
                        View Details
                      </Button>
                    </div>
                  </Card.Body>
                </Card>
              </Col>
            );
          })}
        </Row>
      ) : (
        <NoRacksFound />
      )}
    </div>
  );

  const BinsView = () => {
    if (!selectedRack) return null;

    const binGrid = generateBinGrid(selectedRack);

    useEffect(() => {
      if (currentLocationRef.current && binGrid.length > 0) {
        currentLocationRef.current?.scrollIntoView({
          behavior: "smooth",
          block: "center",
        });
      }
    }, [binGrid.length]);

    return (
      <div style={{ minHeight: "400px" }}>
        {rackBinLoader ? (
          <LoadingSpinner />
        ) : binGrid?.length > 0 ? (
          <div
            className="d-grid gap-3"
            style={{
              gridTemplateColumns: `repeat(${selectedRack.total_columns}, 1fr)`,
              maxWidth: `${selectedRack.total_columns * 160}px`,
            }}
          >
            {binGrid.map((cell) => {
              const isSelected = selectedBins === cell.id;
              const isAnimating = animatingId === cell.id;
              const isOccupiedByAnotherProduct =
                cell.hasProduct && cell.product?.id !== activeProduct?.id;
              const containsActiveProduct = binContainsActiveProduct(
                cell.id,
                cell.bin,
              );
              const isPendingReserved = !containsActiveProduct && !!pendingBinIds[String(cell.id)];
              const reservedByProductName = pendingBinIds[String(cell.id)];
              const filledByOther = isOccupiedByAnotherProduct || isPendingReserved;

              return (
                <div
                  key={cell.key}
                  ref={containsActiveProduct ? currentLocationRef : null}
                  className={`relative bin-cell
                    ${cell.status === "active" ? "position-active" : ""}
                    ${cell.status === "in-active" ? "position-inactive" : ""}
                    ${cell.status === "damaged" ? "position-damaged" : ""}
                    ${filledByOther ? "position-fill" : ""}
                    ${containsActiveProduct ? "position-fill" : ""}
                    ${isSelected ? "selected shadow-lg" : ""}`}
                  onClick={async () => {
                    if (isPendingReserved) return;
                    const isOccupiedByActiveProduct = cell.product?.id === activeProduct?.id;
                    if (
                      cell.status === "active" &&
                      (!cell.hasProduct || containsActiveProduct || isOccupiedByActiveProduct)
                    ) {
                      await handleBinClick(cell.id, containsActiveProduct);
                    }
                  }}
                  style={{
                    minHeight: "140px",
                    borderRadius: "12px",
                    transition: "all 0.3s ease",
                    cursor:
                      isPendingReserved
                        ? "not-allowed"
                        : cell.bin.status === "active" &&
                          (!cell.hasProduct || containsActiveProduct || cell.product?.id === activeProduct?.id)
                          ? "pointer"
                          : "not-allowed",
                    border: isAnimating
                      ? "3px solid transparent"
                      : isSelected
                        ? "3px solid #0d6efd"
                        : containsActiveProduct
                          ? "3px solid #3a57e8"
                          : cell.status === "damaged"
                            ? "3px solid #dc3545"
                            : cell.status === "in-active"
                              ? "3px solid #212529"
                              : cell.status === "active"
                                ? "3px solid #28a745"
                                : "",
                    backgroundColor:
                      filledByOther || cell.status !== "active"
                        ? ""
                        : isSelected
                          ? "#f8f9ff"
                          : containsActiveProduct
                            ? "#fff5f5"
                            : "#ffffff",
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                    padding: "12px",
                    boxShadow:
                      isSelected || containsActiveProduct
                        ? "0 4px 20px rgba(13, 110, 253, 0.2)"
                        : "0 2px 8px rgba(0,0,0,0.06)",
                    transform:
                      isSelected || containsActiveProduct
                        ? "translateY(-2px)"
                        : "translateY(0)",
                  }}
                >
                  {isAnimating && (
                    <div
                      style={{
                        position: "absolute",
                        top: 2, left: 2, right: 2, bottom: 2,
                        backgroundColor: "rgba(255,255,255,0.03)",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        zIndex: 10,
                        borderRadius: "12px",
                      }}
                    >
                      <RotatingLines
                        strokeWidth="4" animationDuration="0.75"
                        width="40" strokeColor="#0d6efd" visible
                      />
                    </div>
                  )}

                  <div className="text-center mb-2">
                    <span className="small fw-bold text-dark">
                      Row{cell.row} Col{cell.col}
                    </span>
                  </div>

                  <div className="text-center">
                    {containsActiveProduct ? (
                      <div>
                        <div
                          className="fw-medium text-dark mb-1"
                          style={{ fontSize: "0.8rem", lineHeight: "1.2" }}
                        >
                          {activeProduct.name}
                        </div>
                        {!isOrderMode && (
                          <small className="text-muted">
                            Qty: {activeProduct.product_quantity}
                          </small>
                        )}
                      </div>
                    ) : isPendingReserved ? (
                      <div>
                        <div className="fw-medium text-dark mb-1" style={{ fontSize: "0.8rem", lineHeight: "1.2" }}>
                          {reservedByProductName}
                        </div>
                      </div>
                    ) : (cell.hasProduct && cell.product?.id !== activeProduct?.id) ? (
                      <div>
                        <span className="product-name">
                          {cell.product?.product_name}
                        </span>
                        {!isOrderMode && (
                          <div>
                            <span className="quantity-text">
                              Qty: {cell.product?.quantity}
                            </span>
                          </div>
                        )}
                      </div>
                    ) : cell.bin.status === "active" ? (
                      <div className="text-muted fw-medium" style={{ fontSize: "0.75rem" }}>
                        Click to choose
                      </div>
                    ) : (
                      <div className="text-muted fw-medium" style={{ fontSize: "0.75rem" }}>
                        <span
                          className={`product-name ${
                            cell.bin.status === "damaged"
                              ? "text-danger fw-bold"
                              : cell.bin.status === "in-active"
                                ? "text-danger fw-bold"
                                : ""
                          }`}
                        >
                          {cell.bin.status === "in-active"
                            ? "In-active bin"
                            : cell.bin.status === "damaged"
                              ? "Damaged bin"
                              : ""}
                        </span>
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <NoBinsFound />
        )}
      </div>
    );
  };

  return (
    <Modal
      show={show}
      onHide={handleModalClose}
      backdrop="true"
      dialogClassName="modal-responsive-width"
      keyboard
    >
      <Modal.Header className="custom-modal-header">
        <Modal.Title className="d-flex align-items-center gap-3">
          <div className="d-flex align-items-center gap-3">
            <div className="p-2 bg-opacity-10 rounded-3">
              {currentView === "bins" ? (
                <ArrowLeft
                  size={32}
                  onClick={handleBackToRacks}
                  className="text-primary"
                  style={{ cursor: "pointer" }}
                />
              ) : (
                <MapPin size={26} className="text-primary" />
              )}
            </div>
            <div className="d-flex flex-column">
              <span className="fw-bold fs-4 text-dark">
                {currentView === "racks"
                  ? "Warehouse Racks"
                  : `${selectedRack?.name} - ${selectedRack?.rack_number}`}
              </span>
              <span className="fs-6 text-muted fw-normal">
                {currentView === "racks" ? (
                  activeProduct?.id ? (
                    <>
                      Select a rack to choose a bin location for{" "}
                      <strong>{activeProduct.name}</strong>
                    </>
                  ) : (
                    "Select a rack to view its bin layout"
                  )
                ) : activeProduct?.id ? (
                  <>
                    Choose a bin location for{" "}
                    <strong>{activeProduct.name}</strong>
                  </>
                ) : (
                  "Click on any bin position to select it"
                )}
              </span>
            </div>
          </div>
        </Modal.Title>
        <button
          type="button"
          className="btn-close-svg"
          onClick={handleModalClose}
          aria-label="Close"
        >
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24">
            <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z" />
          </svg>
        </button>
      </Modal.Header>

      <Modal.Body
        className="p-0"
        style={{ height: "75vh", display: "flex", flexDirection: "column" }}
      >
        {currentView === "racks" && (
          <div className="px-4 border-bottom">
            <Card className="border-0 mb-0">
              <Card.Body className="p-4">
                <Row className="align-items-center">
                  <Col lg={12}>
                    <Row className="g-2">
                      <Col sm={6}>
                        <div className="position-relative w-50">
                          <input
                            type="text"
                            className="form-control"
                            placeholder="Search racks..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                          />
                        </div>
                      </Col>
                      <Col
                        sm={6}
                        className="d-flex ps-0 ps-sm-4 justify-content-start justify-content-md-end align-items-center"
                      >
                        {(rackManagementHasCreatePermission ||
                          decodedToken?.roles?.some(
                            (r) => r.role_type === roleTypes.SUPER_ADMIN,
                          )) && (
                          <Button
                            type="button"
                            variant="btn btn-primary"
                            className="responsive-button"
                            onClick={handleAddNewRack}
                          >
                            Add New Rack
                          </Button>
                        )}
                      </Col>
                    </Row>
                  </Col>
                </Row>
              </Card.Body>
            </Card>
          </div>
        )}

        {currentView === "bins" && (
          <div className="px-4 border-bottom">
            <Card className="border-0 mb-0">
              <Card.Body className="p-4">
                <Row>
                  <Col lg={8}>
                    <div className="d-flex align-items-center gap-4">
                      {[
                        { cls: "border-success", label: "Active" },
                        { cls: "border-dark", label: "Inactive" },
                        { cls: "border-danger", label: "Damage" },
                        {
                          cls: "border-success position-active position-fill",
                          label: "Fill",
                        },
                        {
                          cls: "border-primary position-active position-fill",
                          label: "Current",
                        },
                      ].map(({ cls, label }) => (
                        <div key={label} className="d-flex align-items-center">
                          <div
                            className={`rounded-1 border border-2 ${cls} me-2`}
                            style={{ width: "16px", height: "16px" }}
                          />
                          <span className="small text-dark">{label}</span>
                        </div>
                      ))}
                    </div>
                  </Col>
                </Row>
              </Card.Body>
              {alertResponse?.alertFor === "bin-list" && (
                <AlertNotificationSimple
                  type={alertResponse.type}
                  message={alertResponse.message}
                  show={alertResponse.show}
                  onClose={() =>
                    setAlertResponse?.({ ...alertResponse, show: false })
                  }
                />
              )}
              {alerts?.alertFor === "bin-list" && alerts.show && (
                <AlertNotificationSimple
                  type={alerts.type}
                  message={alerts.message}
                  show={alerts.show}
                  onClose={() =>
                    setAlerts({ show: false, alertFor: "", message: "", type: "" })
                  }
                />
              )}
            </Card>
          </div>
        )}

        <div
          className="flex-grow-1 px-4 py-3"
          style={{ overflowY: "auto", flex: "1 1 auto" }}
        >
          <Container fluid className="px-0">
            {currentView === "racks" ? <RacksView /> : <BinsView />}
          </Container>
        </div>
      </Modal.Body>
    </Modal>
  );
};

export default RackBinModal;